import { Injectable, TemplateRef, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ModalType = 'confirm' | 'custom';

export interface ModalConfig {
  type?: ModalType;
  title?: string;
  message?: string;
  component?: Type<any>;
  template?: TemplateRef<any>;
  props?: any;
  className?: string;
  isFullscreen?: boolean;
  showCloseButton?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private modalSubject = new BehaviorSubject<ModalConfig | null>(null);
  readonly modal$ = this.modalSubject.asObservable();

  // Backwards-compatible simple open/close API used in some components
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  readonly isOpen$ = this.isOpenSubject.asObservable();

  // Internal resolver for confirm dialogs
  private confirmResolver: ((value: boolean) => void) | null = null;

  open(config: ModalConfig) {
    this.modalSubject.next(config);
  }

  close() {
    this.modalSubject.next(null);
    this.confirmResolver = null;
    this.isOpenSubject.next(false);
  }

  // Backwards-compatible methods used by some components in the project
  openModal(): void {
    this.isOpenSubject.next(true);
  }

  closeModal(): void {
    this.isOpenSubject.next(false);
  }

  toggleModal(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  // Opens a confirm dialog and returns a Promise<boolean>
  confirm(options: { title?: string; message?: string; confirmText?: string; cancelText?: string; isFullscreen?: boolean } = {}) {
    return new Promise<boolean>(resolve => {
      this.confirmResolver = resolve;
      this.open({
        type: 'confirm',
        title: options.title ?? 'Confirm',
        message: options.message ?? '',
        className: undefined,
        isFullscreen: options.isFullscreen ?? false,
        showCloseButton: true,
      });
    });
  }

  // Called by the host when user responds to confirm dialog
  resolveConfirm(value: boolean) {
    if (this.confirmResolver) {
      this.confirmResolver(value);
      this.confirmResolver = null;
    }
    this.close();
  }
}

